import { useEffect } from 'react';
/**
 * Enter works like Tab in forms: it moves to the next field; on the last field it moves
 * to the Save button, and Enter on that button saves (the button's own click).
 *
 * Left alone: textarea (new line), buttons, links, an open dropdown (Enter picks the option),
 * search boxes, and any field that already uses Enter itself — mark those with
 * data-enter="keep" (barcode scan, quick search, "save preset", page jump…).
 */
const FIELD_SELECTOR = 'input, select, textarea, [role="combobox"]';
const SKIP_INPUT_TYPES = new Set(['hidden', 'button', 'submit', 'reset', 'file', 'image', 'search']);
const SAVE_TEXT = /^\s*(save|update|create|submit)\b/i;
const NEXT_TEXT = /^\s*next\b/i;
function isVisible(el) {
    return el.offsetParent !== null || el.getClientRects().length > 0;
}
function isNavigable(el) {
    if (el.tagName === 'INPUT' && SKIP_INPUT_TYPES.has(el.type))
        return false;
    if (el.disabled || el.readOnly)
        return false;
    if (el.getAttribute('aria-disabled') === 'true' || el.tabIndex < 0)
        return false;
    return isVisible(el);
}
/** The form area a field belongs to: its popup, else its <form>, else the page. */
export function getEnterContainer(el) {
    return el.closest('[role="dialog"]') || el.closest('form') || el.closest('main');
}
function getFields(container) {
    return Array.from(container.querySelectorAll(FIELD_SELECTOR)).filter(isNavigable);
}
/**
 * The container's Save button: data-enter-save, else a submit button, else a Save / Update /
 * Create / Submit button, else (first step of a multi-step form) a Next button.
 */
export function findSaveButton(container) {
    const buttons = Array.from(container.querySelectorAll('button')).filter((b) => !b.disabled && isVisible(b));
    const text = (b) => b.innerText || b.getAttribute('aria-label') || '';
    return (buttons.find((b) => b.hasAttribute('data-enter-save')) ||
        // explicit type="submit" only — a <button> without a type also reports "submit"
        buttons.find((b) => b.getAttribute('type') === 'submit') ||
        buttons.filter((b) => SAVE_TEXT.test(text(b))).pop() ||
        buttons.filter((b) => NEXT_TEXT.test(text(b))).pop() ||
        null);
}
// Set by useEnterKeyNavigation: sections where Enter keeps its old behaviour
let excludedPath;
/** True on pages where Enter navigation is switched off (e.g. parking). */
export function isEnterNavExcluded() {
    return typeof window !== 'undefined' && !!excludedPath?.(window.location.pathname);
}
/**
 * Move focus from `current` to the next field, or to the Save button after the last field.
 * `fields` may be given by a caller that already knows its field order (AdaptiveForm).
 * Returns true when focus moved.
 */
export function focusNextOrSave(current, fields) {
    const container = getEnterContainer(current);
    // Fields on a hidden tab can't take focus — skip them (AdaptiveForm passes every field it has)
    const list = (fields ?? (container ? getFields(container) : [])).filter((f) => f === current || isVisible(f));
    const idx = list.indexOf(current);
    if (idx === -1)
        return false;
    if (idx < list.length - 1) {
        list[idx + 1].focus();
        return true;
    }
    if (isEnterNavExcluded())
        return false;
    const save = container ? findSaveButton(container) : null;
    if (save) {
        save.focus();
        return true;
    }
    return false;
}
function keepsOwnEnter(el) {
    if (el.closest('[data-enter="keep"]') || el.closest('[data-discard-ignore]'))
        return true;
    if (el.tagName === 'TEXTAREA' || el.isContentEditable)
        return true;
    const placeholder = el.getAttribute('placeholder') || '';
    if (/search/i.test(placeholder))
        return true;
    return false;
}
/**
 * App-wide Enter-to-next-field. Mount once inside the app shell.
 * `isPathExcluded` turns it off for whole sections (e.g. parking, login).
 */
export function useEnterKeyNavigation(isPathExcluded) {
    useEffect(() => {
        excludedPath = isPathExcluded;
        const onKeyDown = (e) => {
            if (e.key !== 'Enter' || e.defaultPrevented || e.isComposing)
                return;
            if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey)
                return;
            if (isPathExcluded?.(window.location.pathname))
                return;
            const target = e.target;
            if (!target || !target.matches(FIELD_SELECTOR) || !isNavigable(target) || keepsOwnEnter(target))
                return;
            // react-select: Enter with the menu open picks an option (it prevents default itself);
            // with the menu closed it means "next field", which is what we do here
            if (focusNextOrSave(target))
                e.preventDefault();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (excludedPath === isPathExcluded)
                excludedPath = undefined;
        };
    }, [isPathExcluded]);
}
